<template>
  <section class="login">
    <form v-if="showLocalProvider" class="form provider" @submit.prevent="submit">
      <div class="form-group">
        <ms-text-field v-model="email" :label="'Email'"></ms-text-field>
      </div>
      <div class="form-group">
        <ms-text-field v-model="password" :label="'Password'" type="password"></ms-text-field>
      </div>

      <div v-if="isRequestFailed" class="form-group error">
        <ms-toast type="error">
          Authentication failed. Check your email/password and try again.
        </ms-toast>
      </div>
      <div class="form-group submit">
        <ms-button color="primary" :disabled="isRequestPending || !isValid">Login</ms-button>
      </div>
    </form>
    <div v-if="hasProvider('microsoft')" class="provider external">
      <button class="default microsoft" :disabled="isRequestPending" @click.stop.prevent="microsoft">Sign in with Microsoft</button>
    </div>
    <div v-if="showOtherLoginOptionsButton" class="provider external other-login-options">
      <button type="button" :disabled="isRequestPending" @click.stop.prevent="showOtherLoginOptions = true">other login options</button>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { dialog } from './../utils/office';
import logger from './../utils/logger';

import TextField from './ms/TextField';
import Button from './ms/Button';
import Toast from './ms/Toast';
export default {
  components: {
    'ms-text-field': TextField,
    'ms-button': Button,
    'ms-toast': Toast
  },
  data() {
    return {
      email: '',
      password: '',
      showOtherLoginOptions: false
    };
  },
  computed: {
    ...mapState({
      isRequestPending: s => s.identity.isRequestPending,
      isRequestFailed: s => s.identity.isRequestFailed,
      providers: s => s.authentication.providers || []
    }),
    isValid() {
      return this.email.length && this.password.length;
    },
    localProvider() {
      return this.providers.find((p) => p.type === 'local');
    },
    showLocalProvider() {
      if (!this.localProvider) {
        return false;
      }

      const isShownByDefault = this.localProvider.showByDefault !== false;
      return isShownByDefault || this.showOtherLoginOptions;
    },
    showOtherLoginOptionsButton() {
      if (!this.localProvider) {
        return false;
      }

      return this.localProvider.showByDefault === false && !this.showOtherLoginOptions;
    }
  },
  async mounted() {
    this.$trackEvent('Login page opened');
  },
  async created() {
    await this.$store.dispatch('authentication/initialize');
  },
  methods: {
    hasProvider(name) {
      return this.providers.some((provider) => provider.type === name);
    },
    async submit() {
      if (!this.isValid) {
        return;
      }

      try {
        await this.$store.dispatch('identity/login', {
          email: this.email,
          password: this.password
        });
        this.$identify(this.email);
        this.$trackEvent('Login Completed');
      } catch (e) {
        this.$trackEvent('Login Failed');
      }
    },
    async microsoft() {
      const lock = this.$lock();

      try {
        const response = await fetch(`/api/auth/microsoft?redirect_uri=${encodeURIComponent(`${document.location.origin}/microsoft.html`)}`);
        if (response.status < 200 || response.status > 300) {
          this.$toast.error({
            title: 'Failed to Sign in with Microsoft',
            message: `Please, try again later or contact our development team.`
          });
          return;
        }
        const url = await response.text();

        const result = await dialog(`${document.location.origin}/microsoft.html?goto=${encodeURIComponent(url)}`);

        if (result && result.code) {
          await fetch('/api/auth/microsoft/identity', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              code: result.code,
              redirect_uri: `${document.location.origin}/microsoft.html`
            })
          })
            .then(response => {
              if (response.status !== 200) {
                throw new Error();
              }

              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                return response.json();
              }

              return response.text();
            })
            .then(({ token }) => {
              localStorage.setItem('HARRITY_AUTH_TOKEN', token);
            });
          this.$trackEvent('Sign in with Microsoft Completed');
        } else {
          this.$trackEvent('Sign in with Microsoft Cancelled');
        }
        await this.$store.dispatch('identity/initialize');
      } catch (e) {
        logger.error(e);

        this.$trackEvent('Sign in with Microsoft Failed');
      } finally {
        lock && lock.release();
      }
    }
  }
};
</script>

<style lang="scss">
.login {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 25% 10%;
  .form {
    .form-group {
      &:not(:last-child) {
        margin-bottom: 1rem;
      }

      &.error {
        width: 100%;
        display: flex;
      }

      &.submit {
        padding-top: 1rem;
        button {
          width: 100%;
        }
      }
    }
  }

  .provider {
    &:not(:last-child) {
      &:not(:last-child) {
        padding-bottom: 1rem;
        margin-bottom: 1rem;
      }
      border-bottom: 1px solid var(--theme-highlight);
    }
  }
  .external {
    display: flex;
    justify-content: center;
    .microsoft {
      display: flex;
      align-items: center;
      height: 41px;
      outline: none;
      &:before {
        content: '';
        background-image: url('~./../assets/microsoft.svg');
        width: 21px;
        height: 21px;
        margin-right: 12px;
        display: block;
      }
      background: #ffffff;
      border: 1px solid #8c8c8c;
      color: #5e5e5e;
      font-weight: 600;
      font-size: 15px;
      line-height: 1;
      text-transform: inherit;
      padding: 0 12px;
      cursor: pointer;

      &[disabled] {
        cursor: default;
        opacity: 0.7;
      }

      &:not([disabled]) {
        &:hover {
          background: var(--theme-highlight) !important;
        }
      }
    }

    &.other-login-options {
      button {
        border: 0;
        background: transparent;
        color: #00a3e0;
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }
}
</style>
